import { Box, TextField, Button } from "@mui/material";
import { useEffect, useState } from "react";
import Table from "../../components/Table";
import { toast } from "react-toastify";
import {
  Edit,
  Visibility,
  Add,
  Delete,
  Download as DownloadIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import theme from "../../theme/theme";
import {
  fetchCustomers,
  fetchCustomerById,
  updateCustomer as updateCustomerApi,
  createCustomer,
  deleteCustomer,
  exportCustomers,
} from "../../api/CustomerService";
import ZoneSelect from "../../components/common/ZoneSelect";
import CitySelect from "../../components/common/CitySelect";
import { useNavigate } from "react-router-dom";
import { EditCustomerDialog } from "../../components/customer/EditCustomerDialog";
import { CreateCustomerDialog } from "../../components/customer/CreateCustomerDialog";
import ConfirmDialog from "../../components/ConfirmDialog";

const CustomersManagementPage = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(15);
  const [links, setLinks] = useState([]);
  const [meta, setMeta] = useState({ from: 0, to: 0, total: 0 });
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [exporting, setExporting] = useState(false);

  // Zone & City filters
  const [selectedZone, setSelectedZone] = useState("");
  const [selectedCity, setSelectedCity] = useState("");

  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [fetchingCustomer, setFetchingCustomer] = useState(false);
  const [updatingCustomer, setUpdatingCustomer] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState({});
  const [errors, setErrors] = useState({});

  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  const [createErrors, setCreateErrors] = useState({});

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const handleCreateCustomer = async (data) => {
    setCreatingCustomer(true);
    setCreateErrors({});
    try {
      await createCustomer(data);
      toast.success("Customer created successfully");
      setOpenCreateDialog(false);
      loadCustomers();
    } catch (error) {
      if (error?.response?.data?.errors) {
        setCreateErrors(error.response.data.errors);
      } else {
        toast.error(error.message || "Failed to create customer");
      }
    } finally {
      setCreatingCustomer(false);
    }
  };

  const editCustomer = (id) => {
    setOpenEditDialog(true);
    fetchCustomerForEdit(id);
  };

  const fetchCustomerForEdit = async (id) => {
    setFetchingCustomer(true);
    try {
      const response = await fetchCustomerById(id);
      setSelectedCustomer(response.data);
    } catch (error) {
      toast.error(error.message || "Failed to fetch customer details for edit");
    } finally {
      setFetchingCustomer(false);
    }
  };

  const updateCustomerData = async () => {
    setUpdatingCustomer(true);
    setErrors({});
    try {
      await updateCustomerApi(selectedCustomer.id, selectedCustomer);
      toast.success("Customer updated successfully");
      setOpenEditDialog(false);
      loadCustomers();
    } catch (error) {
      if (
        error &&
        error.response &&
        error.response.data &&
        error.response.data.errors
      ) {
        setErrors(error.response.data.errors);
      } else {
        toast.error(error?.message || "Failed to update customer");
      }
    } finally {
      setUpdatingCustomer(false);
    }
  };

  const loadCustomers = async () => {
    setLoading(true);
    try {
      const response = await fetchCustomers(
        currentPage,
        searchQuery || null,
        selectedZone || null,
        selectedCity || null,
        perPage,
      );
      setCustomers(response.data.data);
      setLinks(response.data.links);
      setMeta({ from: response.data.from, to: response.data.to, total: response.data.total });
    } catch (error) {
      toast.error("Failed to fetch customers");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await exportCustomers(
        searchQuery || null,
        selectedZone || null,
        selectedCity || null,
      );
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "customers.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      toast.error("Failed to export customers");
    } finally {
      setExporting(false);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setSearchQuery(searchInput);
    setCurrentPage(1);
  };

  const handleDeleteClick = (id) => {
    setDeleteTarget(id);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteCustomer(deleteTarget);
      toast.success("Customer deleted successfully");
      setConfirmOpen(false);
      setDeleteTarget(null);
      loadCustomers();
    } catch (error) {
      toast.error(error?.message || "Failed to delete customer");
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [perPage]);

  useEffect(() => {
    loadCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchQuery, selectedZone, selectedCity, perPage]);

  const actions = [
    {
      icon: <Edit />,
      label: "Edit",
      onClick: (row) => {
        editCustomer(row.id);
      },
      backgroundColor: theme.button.editBackgroundColor,
    },
    {
      icon: <Visibility />,
      label: "View",
      onClick: (row) => {
        navigate(`/customers/${row.id}`);
      },
      backgroundColor: theme.button.viewBackgroundColor,
    },
    {
      icon: <Delete />,
      label: "Delete",
      onClick: (row) => {
        handleDeleteClick(row.id);
      },
      backgroundColor: theme.button.deleteBackgroundColor || "#ff5252",
    },
  ];

  return (
    <Box>
      <Box component="h1" sx={{ m: 0 }}>
        Customers Management
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <form
            onSubmit={handleSearchSubmit}
            style={{ display: "flex", gap: "8px" }}
          >
            <TextField
              size="small"
              placeholder="Search customers..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              sx={{ backgroundColor: "#fff", borderRadius: 1, width: 200 }}
            />
            <Button
              type="submit"
              variant="contained"
              sx={{ minWidth: "40px", p: 1 }}
            >
              <SearchIcon />
            </Button>
          </form>

          {/* Zone filter */}
          <ZoneSelect
            value={selectedZone}
            onChange={(id) => {
              setSelectedZone(id);
              setSelectedCity("");
              setCurrentPage(1);
            }}
            sx={{ minWidth: 170, backgroundColor: "#fff", borderRadius: 1 }}
          />

          {/* City filter */}
          <CitySelect
            zoneId={selectedZone}
            value={selectedCity}
            onChange={(id) => {
              setSelectedCity(id);
              setCurrentPage(1);
            }}
            sx={{ minWidth: 170, backgroundColor: "#fff", borderRadius: 1 }}
          />
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            disabled={exporting}
            sx={{ backgroundColor: "#fff" }}
          >
            {exporting ? "Exporting..." : "Export Excel"}
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            sx={{
              borderRadius: theme.button.borderRadius,
              backgroundColor: theme.palette.primary.main,
            }}
            onClick={() => setOpenCreateDialog(true)}
          >
            {" "}
            Customer
          </Button>
        </Box>
      </Box>
      <Box>
        <Table
          columns={[
            { field: "name", headerName: "Name" },
            { field: "email", headerName: "Email" },
            { field: "phone", headerName: "Phone" },
            { field: "address", headerName: "Address" },
          ]}
          data={customers}
          loading={loading}
          links={links}
          setCurrentPage={setCurrentPage}
          actions={actions}
          meta={meta}
          perPage={perPage}
          setPerPage={setPerPage}
        />
      </Box>
      <EditCustomerDialog
        openEditDialog={openEditDialog}
        setOpenEditDialog={setOpenEditDialog}
        fetchingCustomer={fetchingCustomer}
        selectedCustomer={selectedCustomer}
        setSelectedCustomer={setSelectedCustomer}
        updateCustomer={updateCustomerData}
        errors={errors}
        updatingCustomer={updatingCustomer}
      />
      <CreateCustomerDialog
        open={openCreateDialog}
        onClose={() => setOpenCreateDialog(false)}
        saving={creatingCustomer}
        onSave={handleCreateCustomer}
        errors={createErrors}
      />
      <ConfirmDialog
        open={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
          setDeleteTarget(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Customer"
        message="Are you sure you want to delete this customer? All associated data might be affected. This action cannot be undone."
        loading={deleting}
      />
    </Box>
  );
};

export default CustomersManagementPage;
